import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"

import { loadPaymentMethods, loadPlans, loadUsage } from "../actions"
import { PLAN_TYPE_ORDER } from "../consts"
import {
  CmsPlanItems,
  CmsTeamQuota,
  Plan,
  PlanItem,
  ProductName,
  Quota,
  ServiceName,
} from "../interfaces"
import logger from "../lib/logger"
import { ServiceNameMap } from "../lib/service"
import {
  useCurrentService,
  useCurrentTeam,
  useCurrentTeamQuotas,
  usePaymentMethods,
  usePlans,
  useUsage as useUsageRecoil,
} from "../recoils"

import { useUsage } from "./useUsage"

// プランの上限と使用量を突き合わせる。
//
// ここは防御ではなく表示の一部である。auth-api の
// Api::V2::TeamsController#subscriptions_update は owner / admin のロール判定
// しかせず、その先の Billing::ChangePlan も presence / inclusion しか見ない。
// つまり POST /api/v2/teams/:team_id/subscriptions を直接叩けばこの判定は
// 素通りする。ここを厳しくしても止まるのはブラウザを使う人だけで、代わりに
// カタログが欠けた瞬間に「上限 0」で全チームのプラン変更が止まる。
//
// なので上限が読めないとき（unmeasured）は弾かない。ただし黙っては通さない。
// サーバー側に検証が無い以上、緩めた事実はここのログにしか残らない。
const exceedsPlanQuota = (
  item: PlanItem,
  quota: Quota,
  productName: ProductName,
): boolean => {
  const limit = item.includedQuota

  // unlimited を先に見る。無制限プランを 0 として足すと上限 0 になり、
  // 使用量がいくつであっても「上限超過」でプラン変更が弾かれる（#408）。
  if (limit.kind === "unlimited") return false

  if (limit.kind === "unmeasured") {
    logger(
      "isQuotaExceeded: プランの上限が読めないので超過判定をしませんでした",
      { productName },
      new Error("unmeasured plan quota"),
    )

    return false
  }

  return quota.usedQuota > limit.value + quota.purchasedQuota
}

export const usePlan = () => {
  const currentTeam = useRecoilValue(useCurrentTeam)
  const [currentService, setCurrentService] = useRecoilState(useCurrentService)
  const [usage, setUsage] = useRecoilState(useUsageRecoil)
  const currentTeamQuotas = useRecoilValue(useCurrentTeamQuotas)
  const setPlans = useSetRecoilState(usePlans)
  const setPaymentMethods = useSetRecoilState(usePaymentMethods)
  const { isUnlimitedAbtest } = useUsage()

  const loadPlan = async (serviceName: ServiceName) => {
    const res = await Promise.all([
      loadPaymentMethods(currentTeam.id),
      loadPlans(serviceName),
      loadUsage(currentTeam.id, serviceName),
    ])

    const [paymentMethods, plans, usage] = res
    setPaymentMethods({ list: paymentMethods })
    setPlans({
      list: plans,
    })
    if (usage) {
      setUsage({ ...usage })
    }
    setCurrentService({ name: serviceName })
  }

  // 並び順は PLAN_TYPE_ORDER だけが持つ。ここに独自の配列を置いていたため
  // starter が抜け、indexOf が -1 になって free -> starter（¥0 -> ¥1,500）が
  // 「本当にプランをダウングレードしますか？」と描かれていた。
  const isUpgrade = (currentPlan: Plan, targetPlan: Plan) => {
    return (
      PLAN_TYPE_ORDER.indexOf(currentPlan.planType) <
      PLAN_TYPE_ORDER.indexOf(targetPlan.planType)
    )
  }

  const isQuotaExceeded = (currentPlan: Plan, targetPlan: Plan) => {
    if (currentService.name === ServiceNameMap.CLOUD) return false
    if (isUpgrade(currentPlan, targetPlan)) return false

    const items = targetPlan.items as CmsPlanItems
    const quotas = currentTeamQuotas as CmsTeamQuota

    if (exceedsPlanQuota(items.membership, quotas.membership, "membership")) {
      return true
    }

    if (exceedsPlanQuota(items.form, quotas.form, "form")) {
      return true
    }

    if (isUnlimitedAbtest(targetPlan)) return false

    return usage.contentAbTest.totalCount > 1
  }

  return {
    loadPlan,
    isUpgrade,
    isQuotaExceeded,
  }
}
